// The sign-up / log-in form of the auth door: Google first, then the fields with
// visible labels, per-field messages, one form-level alert for server outcomes
// and one full-width primary (DESIGN-SPEC §6.2, §6.19).
import type { FormEvent, RefObject } from 'react';
import { LockIcon } from 'lucide-react';
import type { EntryCopy } from '@/i18n/ns/entry';
import { PasswordInput } from '@/components/ui/password-input';
import { GoogleButton } from './google-button';
import type { AuthStrings } from './strings';
import { entryErrorText } from './entry-copy';
import { EntryAlert, EntryField } from './entry-field';
import { EntryButton } from './entry-shell';
import type { EntryFieldName, FieldErrors } from './entry-validation';

/**
 * "Signing up as {email}" with the address as one unit: it moves to its own
 * line whole instead of breaking at a hyphen, wherever a language places it.
 */
function SigningUpAs({ template, email }: { template: string; email: string }) {
  const [before, after = ''] = template.split('{email}');
  return (
    <span>
      {before}
      <span className="pc-entry-address">{email}</span>
      {after}
    </span>
  );
}

export interface CredentialsFormState {
  companyName: string;
  email: string;
  password: string;
}

export function CredentialsForm({
  mode,
  creatingCompany,
  boundRecipient,
  showGoogle,
  values,
  fields,
  error,
  busy,
  autoFocusFirstField,
  refs,
  entry,
  auth,
  onChange,
  onSubmit,
  onGoogle,
  onForgot,
}: {
  mode: 'signup' | 'login';
  creatingCompany: boolean;
  boundRecipient: string | null;
  showGoogle: boolean;
  values: CredentialsFormState;
  fields: FieldErrors;
  /** A form-level outcome code (wrong credentials, rate limit, outage). */
  error: string | null;
  busy: boolean;
  autoFocusFirstField: boolean;
  refs: {
    company: RefObject<HTMLInputElement | null>;
    email: RefObject<HTMLInputElement | null>;
    password: RefObject<HTMLInputElement | null>;
  };
  entry: EntryCopy;
  auth: AuthStrings;
  onChange: (field: EntryFieldName, value: string) => void;
  onSubmit: () => void;
  onGoogle: () => void;
  onForgot: () => void;
}) {
  const signup = mode === 'signup';
  const text = (code: string | undefined) => (code ? entryErrorText(code, entry, auth) : null);
  const firstField: EntryFieldName = creatingCompany
    ? 'company'
    : boundRecipient
      ? 'password'
      : 'email';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!busy) onSubmit();
  };
  return (
    <form className="pc-entry-form" noValidate onSubmit={submit}>
      {showGoogle ? (
        <>
          <GoogleButton label={auth.google} onClick={onGoogle} disabled={busy} />
          <div aria-hidden className="pc-entry-divider">
            {auth.divider}
          </div>
        </>
      ) : null}
      {creatingCompany ? (
        <EntryField label={entry.companyLabel} error={text(fields.company)}>
          {(control) => (
            <input
              {...control}
              ref={refs.company}
              type="text"
              autoComplete="organization"
              maxLength={120}
              autoFocus={autoFocusFirstField && firstField === 'company'}
              value={values.companyName}
              onChange={(event) => onChange('company', event.currentTarget.value)}
              className="pc-field-input"
            />
          )}
        </EntryField>
      ) : null}
      {boundRecipient && signup ? (
        <p className="pc-entry-static">
          <LockIcon aria-hidden="true" />
          <SigningUpAs template={entry.signingUpAs} email={boundRecipient} />
        </p>
      ) : boundRecipient ? (
        <div className="pc-field">
          <span className="pc-field-label">{entry.emailLabel}</span>
          <p className="pc-entry-static">
            <LockIcon aria-hidden="true" />
            <span>{boundRecipient}</span>
          </p>
        </div>
      ) : (
        <EntryField
          label={signup ? entry.workEmailLabel : entry.emailLabel}
          error={text(fields.email)}
        >
          {(control) => (
            <input
              {...control}
              ref={refs.email}
              type="email"
              autoComplete="email"
              autoFocus={autoFocusFirstField && firstField === 'email'}
              value={values.email}
              onChange={(event) => onChange('email', event.currentTarget.value)}
              className="pc-field-input"
            />
          )}
        </EntryField>
      )}
      <EntryField
        label={entry.passwordLabel}
        help={signup ? entry.passwordHelp : null}
        error={text(fields.password)}
        aside={
          signup ? null : (
            <button type="button" className="pc-link" data-tone="muted" onClick={onForgot}>
              {entry.forgotPassword}
            </button>
          )
        }
      >
        {(control) => (
          <PasswordInput
            {...control}
            ref={refs.password}
            autoFocus={autoFocusFirstField && firstField === 'password'}
            autoComplete={signup ? 'new-password' : 'current-password'}
            value={values.password}
            onChange={(event) => onChange('password', event.currentTarget.value)}
            className="pc-field-input"
            showLabel={auth.showPassword}
            hideLabel={auth.hidePassword}
          />
        )}
      </EntryField>
      {error ? <EntryAlert>{text(error)}</EntryAlert> : null}
      <EntryButton
        type="submit"
        block
        busy={busy}
        busyLabel={signup ? entry.creatingAccount : entry.loggingIn}
        onClick={(event) => {
          event.preventDefault();
          if (!busy) onSubmit();
        }}
      >
        {signup ? auth.submitSignup : auth.submitLogin}
      </EntryButton>
    </form>
  );
}
